/**
 * BackendFontService - Integration with backend FontRegistry
 * 
 * Instead of trying to load fonts on the frontend, this service communicates
 * with the backend's sophisticated FontRegistry to get available fonts and
 * let the backend handle all font loading and shaping.
 */

export interface BackendFont {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  available: boolean;
}

export interface FontSubstitution {
  requested: string;
  actual: string;
}

export interface BackendFontInfo {
  availableFonts: BackendFont[];
  substitutions: Record<string, string>;
  defaultFonts: {
    serif: string;
    sans: string;
    mono: string;
  };
}

export class BackendFontService {
  private baseUrl: string;
  private token: string;
  private fontInfo: BackendFontInfo | null = null;
  private fontCache: Map<string, BackendFont> = new Map();

  constructor(baseUrl: string, token: string) {
    this.baseUrl = baseUrl;
    this.token = token;
  }

  /**
   * Initialize the service by fetching available fonts from backend
   */
  async initialize(): Promise<void> {
    try {
      console.log('[BackendFontService] Fetching fonts via backend proxy to avoid CORS...');
      
      // Fetch fonts via backend atlas manifest endpoint (avoids CORS issues with direct HarfBuzz access)
      const response = await fetch(`${this.baseUrl}/api/v1/edgedocs/typesetter/atlas/manifest`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'X-Contract-Version': '1.0.0'
        }
      });

      if (!response.ok) {
        // If the endpoint doesn't exist, use the manifest info
        this.fontInfo = this.getDefaultFontInfo();
        console.log('[BackendFontService] Using default font configuration');
        return;
      }

      const data = await response.json();
      
      // Convert backend manifest format to our expected format
      this.fontInfo = this.convertManifestToFontInfo(data);
      
      // Cache fonts for quick lookup
      if (this.fontInfo && this.fontInfo.availableFonts) {
        this.fontInfo.availableFonts.forEach(font => {
          const key = `${font.family}-${font.weight}-${font.style}`;
          this.fontCache.set(key, font);
        });

        console.log('[BackendFontService] Loaded fonts from backend:', this.fontInfo.availableFonts.length);
      }
      
    } catch (error) {
      console.log('[BackendFontService] Failed to fetch fonts from backend, using defaults:', error);
      this.fontInfo = this.getDefaultFontInfo();
    }
  }

  /**
   * Convert backend atlas manifest format to our expected format
   */
  private convertManifestToFontInfo(manifest: any): BackendFontInfo {
    const availableFonts: BackendFont[] = [];
    
    // Convert backend atlas manifest families to our font format
    // Include all variants for proper dropdown display
    if (manifest.families) {
      Object.entries(manifest.families).forEach(([familyName, familyData]: [string, any]) => {
        if (familyData.variants) {
          familyData.variants.forEach((variant: any) => {
            availableFonts.push({
              family: variant.family || familyName,
              weight: variant.weight || 400,
              style: variant.style === 'italic' ? 'italic' : 'normal',
              available: true
            });
          });
        }
      });
    }
    
    return {
      availableFonts,
      substitutions: {
        'serif': 'Times, "Times New Roman", serif',
        'sans-serif': 'Times, "Times New Roman", serif', 
        'monospace': 'Times, "Times New Roman", serif',
        'Times New Roman': 'Times, "Times New Roman", serif', // Use system Times
        'Arial': 'Times, "Times New Roman", serif',
        'Helvetica': 'Times, "Times New Roman", serif',
        'Courier New': 'Times, "Times New Roman", serif',
        'Inter': 'Times, "Times New Roman", serif' // Map Inter to system Times
      },
      defaultFonts: {
        serif: 'Times, "Times New Roman", serif',
        sans: 'Times, "Times New Roman", serif',
        mono: 'Times, "Times New Roman", serif'
      }
    };
  }

  /**
   * Get default font info based on the manifest
   */
  private getDefaultFontInfo(): BackendFontInfo {
    return {
      availableFonts: [
        { family: 'Times New Roman', weight: 400, style: 'normal', available: true },
        { family: 'Times New Roman', weight: 400, style: 'italic', available: true },
        { family: 'Times New Roman', weight: 700, style: 'normal', available: true },
        { family: 'JetBrains Mono', weight: 400, style: 'normal', available: true },
        { family: 'Noto Naskh Arabic', weight: 400, style: 'normal', available: true }
      ],
      substitutions: {
        'serif': 'Times New Roman',
        'sans-serif': 'Times New Roman', 
        'monospace': 'Times New Roman',
        'Times New Roman': 'Times New Roman',
        'Arial': 'Times New Roman',
        'Helvetica': 'Times New Roman',
        'Courier New': 'Times New Roman',
        'Inter': 'Times New Roman',
        'Source Serif 4': 'Times New Roman'
      },
      defaultFonts: {
        serif: 'Times New Roman',
        sans: 'Times New Roman',
        mono: 'Times New Roman'
      }
    };
  }

  /**
   * Check if a font is available
   */
  isFontAvailable(family: string, weight: number = 400, style: 'normal' | 'italic' = 'normal'): boolean {
    if (!this.fontInfo) return false;
    
    const key = `${family}-${weight}-${style}`;
    return this.fontCache.has(key);
  }

  /**
   * Get the best available font for a request
   */
  resolveFontRequest(family: string, weight: number = 400, style: 'normal' | 'italic' = 'normal'): BackendFont {
    if (!this.fontInfo) {
      throw new Error('BackendFontService not initialized');
    }

    // Check if exact font is available
    const key = `${family}-${weight}-${style}`;
    if (this.fontCache.has(key)) {
      return this.fontCache.get(key)!;
    }

    // Check substitutions
    const substituted = this.fontInfo.substitutions[family];
    if (substituted) {
      const subKey = `${substituted}-${weight}-${style}`;
      if (this.fontCache.has(subKey)) {
        console.log(`[BackendFontService] Using substitution: ${family} -> ${substituted}`);
        return this.fontCache.get(subKey)!;
      }
    }

    // No fallback - fail fast if font not found
    console.error(`[BackendFontService] Font not found and no fallback allowed: ${family}`);
    throw new Error(`Font "${family}" not available and fallbacks disabled`);
  }

  /**
   * Get all available fonts
   */
  getAvailableFonts(): BackendFont[] {
    return this.fontInfo?.availableFonts || [];
  }

  /**
   * Get font substitutions
   */
  getSubstitutions(): Record<string, string> {
    return this.fontInfo?.substitutions || {};
  }

  /**
   * Get CSS font family string for a font request
   */
  getCSSFontFamily(family: string): string {
    const resolved = this.resolveFontRequest(family);
    
    // Return only the resolved font - no CSS fallbacks
    return `"${resolved.family}"`;
  }
}
